/**
 * MIDI Settings card — request Web MIDI, list connected devices, pick a
 * preset (or accept the auto-detected one), and run "MIDI Learn" to bind
 * unmapped controls to deck actions.
 *
 * Every interactive element carries a `data-testid` for playwright.
 */
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Radio, Loader2, Trash2, Sliders } from "lucide-react";
import { useMidiContext } from "@/context/MidiContext";
import { PIONEER_PRESETS, ACTION_CATALOG } from "@/lib/midi/presets";

const DECK_LABELS = ["A", "B", "C", "D"];

export default function MidiSettingsCard() {
  const midi = useMidiContext();
  const [learnDeck, setLearnDeck] = useState(0);
  const [learnAction, setLearnAction] = useState("play");

  const mappingRows = Object.entries(midi.mappings).sort(([a], [b]) => a.localeCompare(b));

  const startLearn = () => {
    midi.setLearnTarget({ deck: learnDeck, action: learnAction });
    toast.info(`Touch a control on your controller to bind → Deck ${DECK_LABELS[learnDeck]} · ${learnAction}`);
  };

  const cancelLearn = () => {
    midi.setLearnTarget(null);
  };

  return (
    <div className="rounded-2xl border border-[#1E2638] bg-[#121620] p-6" data-testid="midi-settings-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#00F0FF] flex items-center gap-2">
            <Radio className="w-3 h-3"/> // Pioneer DJ + MIDI hardware
          </div>
          <div className="font-display font-black text-2xl mt-1">Controller</div>
        </div>
        {midi.status === "ready" && (
          <span data-testid="midi-status-ready" className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30">
            {midi.inputs.length} connected
          </span>
        )}
      </div>

      {/* Access gate */}
      {midi.status === "idle" && (
        <div>
          <p className="text-sm text-[#94A3B8] mb-3 leading-relaxed">
            NeuralDeck talks to Pioneer DJ gear (and every other MIDI-class controller) over the browser's Web MIDI API.
            Click <b className="text-white">Enable MIDI</b> to grant access — takes a second and only asked once per browser.
          </p>
          <Button data-testid="midi-enable-button" onClick={midi.requestAccess}
                  className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest">
            Enable MIDI
          </Button>
        </div>
      )}

      {midi.status === "requesting" && (
        <div className="text-sm text-[#94A3B8] flex items-center gap-2" data-testid="midi-requesting">
          <Loader2 className="w-4 h-4 animate-spin"/> Requesting MIDI access…
        </div>
      )}

      {midi.status === "denied" && (
        <div className="rounded-lg border border-[#FF2A6D]/40 bg-[#FF2A6D]/5 p-3 text-sm text-[#F8FAFC]" data-testid="midi-denied">
          MIDI permission denied. Reload and allow it, or check your browser's site permissions.
        </div>
      )}

      {midi.status === "unsupported" && (
        <div className="rounded-lg border border-[#FFC107]/40 bg-[#FFC107]/5 p-3 text-sm text-[#F8FAFC]" data-testid="midi-unsupported">
          Your browser doesn't support Web MIDI. Use <b>Chrome</b>, <b>Edge</b>, or <b>Opera</b> — Firefox and Safari haven't shipped it yet.
        </div>
      )}

      {midi.status === "ready" && (
        <div className="space-y-4">
          {/* Devices */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mb-1.5">Detected devices</div>
            {midi.inputs.length === 0 ? (
              <div className="text-xs text-[#94A3B8] italic" data-testid="midi-no-devices">
                No MIDI inputs seen. Plug in your Pioneer controller, toggle it to MIDI mode, then check back — this list refreshes automatically.
              </div>
            ) : (
              <ul className="space-y-1.5" data-testid="midi-device-list">
                {midi.inputs.map((inp) => (
                  <li key={inp.id} data-testid={`midi-device-${inp.id}`}
                      className="flex items-center gap-2 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]"/>
                    <span className="text-[#F8FAFC] font-mono">{inp.name}</span>
                    <span className="text-[10px] text-[#94A3B8] font-mono uppercase tracking-widest">{inp.manufacturer || "midi"}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Preset picker */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mb-1.5">Controller preset</div>
            <select data-testid="midi-preset-select"
                    value={midi.detectedModel?.id || ""}
                    onChange={(e) => {
                      const p = PIONEER_PRESETS.find(x => x.id === e.target.value);
                      midi.setModel(p || null);
                      if (p) toast.success(`Applied ${p.label} preset`);
                    }}
                    className="w-full bg-[#0B0E14] border border-[#1E2638] rounded px-3 py-2 text-sm font-mono text-white focus:outline-none focus:border-[#00F0FF]">
              <option value="">— Auto-detect / MIDI Learn only —</option>
              {PIONEER_PRESETS.map(p => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
            {midi.detectedModel && (
              <div className="text-[10px] font-mono text-[#10B981] mt-1" data-testid="midi-detected-model">
                Active preset: {midi.detectedModel.label}
              </div>
            )}
          </div>

          {/* MIDI Learn */}
          <div className="rounded-lg border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] mb-2 flex items-center gap-1.5">
              <Sliders className="w-3 h-3"/> MIDI Learn
            </div>
            {!midi.learnTarget ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
                <label className="text-xs text-[#94A3B8] flex flex-col gap-1">
                  Deck
                  <select data-testid="midi-learn-deck" value={learnDeck} onChange={e => setLearnDeck(Number(e.target.value))}
                          className="bg-[#0B0E14] border border-[#1E2638] rounded px-2 py-1.5 text-white text-sm font-mono">
                    {DECK_LABELS.map((l, i) => <option key={i} value={i}>Deck {l}</option>)}
                    <option value={-1}>Global (crossfader)</option>
                  </select>
                </label>
                <label className="text-xs text-[#94A3B8] flex flex-col gap-1">
                  Action
                  <select data-testid="midi-learn-action" value={learnAction} onChange={e => setLearnAction(e.target.value)}
                          className="bg-[#0B0E14] border border-[#1E2638] rounded px-2 py-1.5 text-white text-sm font-mono">
                    {ACTION_CATALOG.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
                  </select>
                </label>
                <Button data-testid="midi-learn-start" onClick={startLearn}
                        className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest text-xs">
                  Start Learn
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3" data-testid="midi-learn-pending">
                <div className="text-sm text-[#F8FAFC]">
                  <div className="font-black">Touch a control now…</div>
                  <div className="text-xs text-[#94A3B8] mt-0.5">Deck {midi.learnTarget.deck === -1 ? "Global" : DECK_LABELS[midi.learnTarget.deck]} · {midi.learnTarget.action}</div>
                </div>
                <Button data-testid="midi-learn-cancel" onClick={cancelLearn} variant="ghost"
                        className="text-[#94A3B8] hover:text-white text-xs">Cancel</Button>
              </div>
            )}
            {midi.lastEvent && (
              <div className="text-[10px] font-mono text-[#94A3B8] mt-2" data-testid="midi-last-event">
                last event: {midi.lastEvent.sig} · {midi.lastEvent.kind === "cc" ? `value ${midi.lastEvent.value}` : midi.lastEvent.kind === "noteon" ? `note-on vel ${midi.lastEvent.velocity}` : midi.lastEvent.kind}
              </div>
            )}
          </div>

          {/* Custom mappings table */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8]">Custom overrides ({mappingRows.length})</div>
              {mappingRows.length > 0 && (
                <button data-testid="midi-clear-mappings" onClick={() => { midi.clearMappings(); toast.success("Cleared all custom mappings"); }}
                        className="text-[10px] font-mono uppercase tracking-widest text-[#FF2A6D] hover:underline">Clear all</button>
              )}
            </div>
            {mappingRows.length === 0 ? (
              <div className="text-xs text-[#94A3B8] italic" data-testid="midi-mappings-empty">
                No custom overrides. The active preset handles standard controls out of the box.
              </div>
            ) : (
              <div className="space-y-1" data-testid="midi-mappings-list">
                {mappingRows.map(([sig, target]) => (
                  <div key={sig} data-testid={`midi-mapping-${sig}`}
                       className="flex items-center gap-2 px-2 py-1.5 rounded bg-[#0B0E14] border border-[#1E2638] text-[10.5px]">
                    <span className="font-mono text-[#00F0FF]">{sig}</span>
                    <span className="text-[#94A3B8]">→</span>
                    <span className="text-[#F8FAFC] font-mono">Deck {target.deck === -1 ? "Global" : DECK_LABELS[target.deck]} · {target.action}</span>
                    <button data-testid={`midi-mapping-remove-${sig}`} onClick={() => midi.removeMapping(sig)}
                            className="ml-auto text-[#94A3B8] hover:text-[#FF2A6D] transition">
                      <Trash2 className="w-3 h-3"/>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="text-[10.5px] font-mono text-[#94A3B8]/80 leading-relaxed pt-2 border-t border-[#1E2638]">
            Compatibility: any Pioneer controller from <b className="text-white">2006 onward</b> that speaks MIDI (all DDJs, plus CDJs / XDJs flipped to MIDI mode), plus <b className="text-white">Technics SL-1200MK7 / SL-1210MK7</b> (2019+). For two turntables, plug both in and pick "→ Deck B" from the preset dropdown after the second unit is detected.
            <br/>
            Classic Technics SL-1200 (MK2/MK3/MK5/G/GR/GAE) and any CDJ / XDJ stuck in Pioneer's proprietary <b className="text-white">HID</b> or <b className="text-white">Pro DJ Link</b> mode are pure analog / non-MIDI — those need a DVS interface (Rane Twelve, Denon VL12, Serato timecode) or a native driver.
          </div>
        </div>
      )}
    </div>
  );
}
